import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Pause, Pencil, Play, Plus, Trash2 } from 'lucide-react';
import { Fragment, type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { type RecurrenceView } from '@apm/contracts';
import { Button } from '../../../components/ui/button';
import { useConfirmPopover } from '../../../components/ui/confirm-popover';
import { EmptyState, ErrorState } from '../../../components/ui/states';
import { displayDate } from '../../../lib/date';
import { errorMessage } from '../../../lib/error-message';
import { cn } from '../../../lib/utils';
import { recurrenceApi, recurrenceKeys } from '../api';
import { ruleSummary } from '../recurrence';
import { RecurrenceFormDialog } from './recurrence-form-dialog';

/**
 * Cách nhìn "Định kỳ" của tab Công việc — APM-REC-b, chỉ có ở dự án bảo trì.
 *
 * Một dòng một mẫu. Bấm vào dòng thì mở lịch sử các lần đã sinh — mỗi lần là
 * một task thật, bấm vào thì mở đúng task ấy. "Đang tồn" khác 0 hiện đỏ: lần
 * trước chưa xong mà lần mới đã tới là dấu hiệu rõ nhất rằng việc định kỳ đang
 * bị bỏ lơ.
 *
 * ⚠️ Không nhận asOf: danh sách **mẫu** là cấu hình hiện tại của dự án, không
 * phải một đại lượng vẽ theo thời gian. Các lần đã sinh là task, và task đã có
 * lịch sử riêng ở Replay.
 */
export function RecurrencePanel({
  projectId,
  canManage,
  onOpenTask,
}: {
  readonly projectId: string;
  /** `project:update` — cùng quyền API đòi cho mọi lệnh ghi mẫu. */
  readonly canManage: boolean;
  readonly onOpenTask: (taskId: string) => void;
}): ReactNode {
  const { t } = useTranslation();
  const confirm = useConfirmPopover();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<RecurrenceView | 'new' | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  const list = useQuery({
    queryKey: recurrenceKeys.list(projectId),
    queryFn: () => recurrenceApi.list(projectId),
  });

  const onList = (next: RecurrenceView[]): void => {
    queryClient.setQueryData(recurrenceKeys.list(projectId), next);
  };
  const setActive = useMutation({
    mutationFn: (row: RecurrenceView) => recurrenceApi.setActive(projectId, row.id, !row.isActive),
    onSuccess: onList,
    onError: (error) => toast.error(errorMessage(error, t)),
  });
  const remove = useMutation({
    mutationFn: (row: RecurrenceView) => recurrenceApi.remove(projectId, row.id),
    onSuccess: (next) => {
      onList(next);
      toast.success(t('recurrence.deleted'));
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  const rows = list.data ?? [];

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-sm text-muted-foreground">{t('recurrence.intro')}</p>
        {canManage ? (
          <Button size="sm" variant="outline" className="ml-auto" onClick={() => setEditing('new')}>
            <Plus aria-hidden />
            {t('recurrence.new')}
          </Button>
        ) : null}
      </div>

      {list.isError ? (
        <ErrorState message={errorMessage(list.error, t)} onRetry={() => void list.refetch()} />
      ) : list.isPending ? null : rows.length === 0 ? (
        <EmptyState
          title={t('recurrence.emptyTitle')}
          description={canManage ? t('recurrence.emptyHelp') : t('recurrence.emptyHelpReadOnly')}
        />
      ) : (
        <div className="overflow-x-auto rounded-DEFAULT border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
              <tr>
                <th className="px-3 py-2 font-medium">{t('recurrence.col.title')}</th>
                <th className="px-3 py-2 font-medium">{t('recurrence.col.rule')}</th>
                <th className="px-3 py-2 font-medium">{t('recurrence.col.assignee')}</th>
                <th className="px-3 py-2 font-medium">{t('recurrence.col.last')}</th>
                <th className="px-3 py-2 font-medium">{t('recurrence.col.next')}</th>
                <th className="px-3 py-2 text-right font-medium">{t('recurrence.col.pending')}</th>
                {canManage ? <th className="px-3 py-2" /> : null}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const open = expanded === row.id;
                const last = row.occurrences[0] ?? null;
                return (
                  <Fragment key={row.id}>
                    <tr
                      className={cn(
                        'cursor-pointer border-t border-border hover:bg-muted/40',
                        !row.isActive && 'text-muted-foreground',
                      )}
                      aria-expanded={open}
                      onClick={() => setExpanded(open ? null : row.id)}
                    >
                      <td className="px-3 py-2">
                        <span className="flex items-center gap-1 font-medium">
                          {open ? (
                            <ChevronDown aria-hidden className="size-4 shrink-0" />
                          ) : (
                            <ChevronRight aria-hidden className="size-4 shrink-0" />
                          )}
                          {row.title}
                          {row.isActive ? null : (
                            <span className="rounded-DEFAULT bg-muted px-1.5 text-xs font-normal">
                              {t('recurrence.paused')}
                            </span>
                          )}
                        </span>
                        <span className="pl-5 text-xs text-muted-foreground">
                          {row.taskTypeName}
                        </span>
                      </td>
                      <td className="px-3 py-2">{ruleSummary(row.rule, t)}</td>
                      <td className="px-3 py-2">{row.assigneeName ?? '—'}</td>
                      <td className="px-3 py-2 tabular-nums">
                        {last ? displayDate(last.occurrenceDate) : '—'}
                      </td>
                      <td className="px-3 py-2 tabular-nums">
                        {row.nextDate ? displayDate(row.nextDate) : '—'}
                      </td>
                      <td
                        className={cn(
                          'px-3 py-2 text-right tabular-nums',
                          row.pendingCount > 0 && 'font-medium text-destructive',
                        )}
                      >
                        {row.pendingCount}
                      </td>
                      {canManage ? (
                        <td className="px-3 py-2">
                          {/* Nút trong dòng bấm được: chặn nổi bọt để không mở/đóng lịch sử. */}
                          <div
                            className="flex justify-end gap-1"
                            onClick={(event) => event.stopPropagation()}
                          >
                            <Button
                              size="icon"
                              variant="ghost"
                              aria-label={t('common.edit')}
                              title={t('common.edit')}
                              onClick={() => setEditing(row)}
                            >
                              <Pencil aria-hidden />
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              aria-label={
                                row.isActive ? t('recurrence.pause') : t('recurrence.resume')
                              }
                              title={row.isActive ? t('recurrence.pause') : t('recurrence.resume')}
                              disabled={setActive.isPending}
                              onClick={() => setActive.mutate(row)}
                            >
                              {row.isActive ? <Pause aria-hidden /> : <Play aria-hidden />}
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              aria-label={t('common.delete')}
                              title={t('common.delete')}
                              disabled={remove.isPending}
                              onClick={() => {
                                confirm.ask({
                                  message: t('recurrence.confirmDelete', { title: row.title }),
                                  onConfirm: () => remove.mutate(row),
                                });
                              }}
                            >
                              <Trash2 aria-hidden />
                            </Button>
                          </div>
                        </td>
                      ) : null}
                    </tr>
                    {open ? (
                      <tr className="border-t border-border bg-muted/20">
                        <td colSpan={canManage ? 7 : 6} className="px-3 py-2">
                          <Occurrences row={row} onOpenTask={onOpenTask} />
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editing !== null ? (
        <RecurrenceFormDialog
          projectId={projectId}
          {...(editing === 'new' ? {} : { recurrence: editing })}
          onClose={() => setEditing(null)}
        />
      ) : null}
      {confirm.popover}
    </div>
  );
}

/** Lịch sử các lần đã sinh — mới nhất trước, tối đa 20 (API cắt). */
function Occurrences({
  row,
  onOpenTask,
}: {
  readonly row: RecurrenceView;
  readonly onOpenTask: (taskId: string) => void;
}): ReactNode {
  const { t } = useTranslation();
  if (row.occurrences.length === 0) {
    return <p className="text-xs text-muted-foreground">{t('recurrence.noOccurrences')}</p>;
  }
  return (
    <ul className="flex flex-col gap-1">
      {row.occurrences.map((occurrence) => (
        <li key={occurrence.taskId}>
          <button
            type="button"
            className="flex w-full flex-wrap items-center gap-x-3 rounded-DEFAULT px-2 py-1 text-left text-xs hover:bg-muted"
            onClick={() => onOpenTask(occurrence.taskId)}
          >
            <span className="font-mono text-muted-foreground">{occurrence.key}</span>
            <span className="tabular-nums">
              {t('recurrence.occurrenceOn', { date: displayDate(occurrence.occurrenceDate) })}
            </span>
            {occurrence.dueDate && occurrence.dueDate !== occurrence.occurrenceDate ? (
              <span className="tabular-nums text-muted-foreground">
                {t('recurrence.dueOn', { date: displayDate(occurrence.dueDate) })}
              </span>
            ) : null}
            <span className={occurrence.done ? 'text-muted-foreground' : 'font-medium'}>
              {occurrence.statusName}
            </span>
            <span className="text-muted-foreground">{occurrence.assigneeName ?? '—'}</span>
          </button>
        </li>
      ))}
    </ul>
  );
}
